import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { translate } from './translateFunction';
import './UserRequest.css'; // CSS-Datei für den Tooltip und andere Styles

const Feedback = () => {
  const [searchParams] = useSearchParams();
  const [company, setCompany] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [rating, setRating] = useState(null);
  const [translations, setTranslations] = useState(null);
  const [showProgressBar, setShowProgressBar] = useState(false);
  // eslint-disable-next-line
  const [tooltipActive, setTooltipActive] = useState(false);

  const handleMouseEnter = (e) => {
    setTooltipActive(true);
    e.target.removeAttribute('title'); // Entferne das title-Attribut
  };

  const handleMouseLeave = (e) => {
    setTooltipActive(false);
    e.target.setAttribute('title', e.target.dataset.originalTitle); // Setze das title-Attribut zurück
  };

  const fetchCustomerFeedback = useCallback(async (feedbackId) => {
    try {
      const response = await axios.get(`/api/customerFeedback/${feedbackId}`);
      if (response.data) {
        if (response.data.customerFdbckAnswered === 1) {
          setSubmitted(true);
        } else {
          setCompany(response.data.customerCompany);
          setContactPerson(response.data.customerName);
        }
      } else if (translations) {
        setError(translations.invalidLink);
      }
    } catch (error) {
      if (translations) {
        setError(translations.errorOccurred);
      }
    }
  }, [translations]);

  useEffect(() => {
    const loadTranslations = async () => {
      const invalidLink = await translate(410);
      const feedbackFormTitle = await translate(411);
      const customerCompanyLabel = await translate(412);
      const contactPersonLabel = await translate(413);
      const feedbackPlaceholder = await translate(414);
      const submitButton = await translate(415);
      const successMessage = await translate(416);
      const errorOccurred = await translate(417);
      const ratingLabel = await translate(423);
      const veryPositive = await translate(182);
      const positive = await translate(183);
      const neutral = await translate(184);
      const negative = await translate(185);
      const veryNegative = await translate(186);
      const firstHover = await translate(424);
      const secondHover = await translate(425);

      setTranslations({
        invalidLink,
        feedbackFormTitle,
        customerCompanyLabel,
        contactPersonLabel,
        feedbackPlaceholder,
        submitButton,
        successMessage,
        errorOccurred,
        ratingLabel,
        veryPositive,
        positive,
        neutral,
        negative,
        veryNegative,
        firstHover,
        secondHover
      });
    };

    loadTranslations();
  }, []);

  useEffect(() => {
    const feedbackId = searchParams.get('id');
    if (feedbackId) {
      fetchCustomerFeedback(feedbackId);
    } else {
      setError(translations?.invalidLink || 'Invalid feedback link.');
    }
  }, [searchParams, fetchCustomerFeedback, translations?.invalidLink]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const feedbackId = searchParams.get('id');
    setShowProgressBar(true);

    try {
      await axios.put(
        `/api/customerFeedback/text/${feedbackId}`,
        {
          customerFdbckText: feedback,
          customerFdbckReceived: new Date(),
          customerFdbckAnswered: 1,
          rating,
        }
      );
      setSubmitted(true);
    } catch (error) {
      setError(translations?.errorOccurred || 'An error occurred.');
    }
  };

  if (error) {
    return <div>{error}</div>;
  }

  if (!translations) {
    return <div>Lade...</div>;
  }

  return (
    <div className="container">
      {showProgressBar && <div className="progress-bar"></div>}
      {!submitted ? (
        <div className="feedback-form-container">
          <h2>{translations.feedbackFormTitle}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="company">{translations.customerCompanyLabel}</label>
              <input type="text" id="company" className="form-control" value={company} readOnly />
            </div>
            <div className="form-group">
              <label htmlFor="contactPerson">{translations.contactPersonLabel}</label>
              <input type="text" id="contactPerson" className="form-control" value={contactPerson} readOnly />
            </div>
            <div className="form-group">
              <label htmlFor="feedback">
                {/* Ihr Feedback */}
                {translations.feedbackPlaceholder}
                <span
                  className="infoIcon"
                  // data-original-title="Bitte geben Sie Ihr Feedback ein. Teilen Sie uns mit, wie zufrieden Sie sind und was verbessert werden könnte."
                  // title="Bitte geben Sie Ihr Feedback ein. Teilen Sie uns mit, wie zufrieden Sie sind und was verbessert werden könnte."
                  data-original-title={translations.firstHover}
                  title={translations.firstHover}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  ℹ️
                </span>
              </label>
              <textarea
                id="feedback"
                className="form-control"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder={translations.feedbackPlaceholder}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="rating">
                {/* Bewertung (Optional) */}
                {translations.ratingLabel}
                <span
                  className="infoIcon"
                  // data-original-title="Wählen Sie eine Bewertung aus, um uns mitzuteilen, wie zufrieden Sie sind."
                  // title="Wählen Sie eine Bewertung aus, um uns mitzuteilen, wie zufrieden Sie sind."
                  data-original-title={translations.secondHover}
                  title={translations.secondHover}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  ℹ️
                </span>
              </label>
              <div className="rating-container">
                {[-5, -3, 0, 3, 5].map((value, index) => (
                  <div key={value} className="rating-item">
                    <img
                      className="rating-icon"
                      src={`/Content/themes/base/images/${index === 0
                        ? 'VeryNegative.png'
                        : index === 1
                          ? 'Negative.png'
                          : index === 2
                            ? 'unknown.png'
                            : index === 3
                              ? 'Positive.png'
                              : 'VeryPositive.png'
                        }`}
                      alt=""
                    />
                    <label className="rating-radio-label">
                      <input
                        type="radio"
                        name="rating"
                        value={value}
                        onChange={() => setRating(value)}
                      />
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <button type="submit" className="btn btn-primary">
              {translations.submitButton}
            </button>
          </form>
        </div>
      ) : (
        <div className="feedback-success-message transition" style={{ textAlign: 'center' }}>
          <div className="checkmark">&#10003;</div>
          <h3>{translations.successMessage}</h3>
        </div>
      )}
    </div>
  );
};

export default Feedback;
